import { accessTokenStore, clientPublicStore, pageOptsStore } from '@packages/stores'
import type { TBasicPaymentData } from './order.types'
import { get } from 'svelte/store'
import { configStore } from '@src/stores/config'
import type { TPaymentReturn } from '@packages/utils/types'
import { getCompmletedOrderDetails } from '@lib/orders'
import { enrichAssetsWithMetadata, requestSuiReceipt } from './Payment.service'
import { dispatch, PassportEvents } from '@lib/events/events'
import type { TPaymentAsset } from './payment.constants'

export const createOrder = async (paymentData: TBasicPaymentData, idempotencyKey?: string): Promise<string> => {
  const { assets, ...rest } = paymentData
  const assetGroups = mapAssetsToGroups(assets)
  const res = await fetch(get(configStore).credenza.suiApiUrl + '/assets/buy', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${get(accessTokenStore)}`,
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    method: 'POST',
    body: JSON.stringify({ ...rest, ...assetGroups }),
  })
  if (!res.ok) throw new Error('Failed to create order')
  const { _id }: { _id: string } = await res.json()
  return _id
}

export const processOrder = async (orderId: string): Promise<TPaymentReturn> => {
  const config = get(configStore)
  const { title, subtitle } = get(pageOptsStore)
  const data = (await getCompmletedOrderDetails(config.credenza.generalApiUrl, orderId)) as TPaymentReturn
  if (data.items[0].error) throw new Error(data.items[0].error)

  const paymentIntentId = data.payment_provider_tx
  if (paymentIntentId && data.payment_provider !== 'paystack' && data.status === 'COMPLETED') {
    await requestSuiReceipt({ paymentIntentId, title, subtitle, currency: data.currency })
    dispatch(PassportEvents.RECEIPT, { paymentIntentId })
  }
  await enrichAssetsWithMetadata(data.items)
  return data
}

const mapAssetsToGroups = (assets: TPaymentAsset[]) => {
  const typeMap: { [key: string]: string } = {
    credenza_asset_collection: 'collection',
    decom: 'decom',
    cred: 'coin',
    membership: 'membership',
  }

  const clientFee = get(clientPublicStore).client_payment_configuration?.token_fee

  const result = assets.reduce(
    (acc: { [key: string]: { asset_type: string; asset_id?: number; amount?: number }[] }, asset) => {
      const key = typeMap[asset.type]
      if (!key) return acc

      const entry: { asset_type: string; asset_id?: number; amount?: number } = {
        asset_type: `${asset.packageId}::${asset.type}::${asset.type.toUpperCase()}`,
      }

      if (asset.assetId) {
        entry.asset_id = +asset.assetId
      }
      if (asset.amount) {
        entry.amount = asset.amount

        if (asset.type === 'cred' && clientFee) {
          entry.amount = entry.amount * (1 + clientFee)
        }
      }

      acc[key] = acc[key] || []
      acc[key].push(entry)

      return acc
    },
    { collection: [], decom: [], coin: [], membership: [] },
  )

  return result
}
